import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import {
  Country,
  Currency,
  UsersSelf,
} from '@freelancer/datastore/collections';
import { Deposits } from '@freelancer/deposits';
import { Location } from '@freelancer/location';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import {
  DepositForm,
  methodSelectionRadioControl,
  PaymentsUtils,
} from '@freelancer/payments-utils';
import { HeadingType, HeadingWeight } from '@freelancer/ui/heading';
import { ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay, PictureImage } from '@freelancer/ui/picture';
import { SelectItem } from '@freelancer/ui/select';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { pattern, required } from '@freelancer/ui/validators';
import { isDefined, isFormControl, toNumber } from '@freelancer/utils';
import {
  DepositInvoiceApi,
  DepositMethodApi,
  DepositRequestApi,
  DepositRequestConfirmationApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { SourceDisplayMode } from '../../../../common/source-template/source-template.component';
import { DepositData, GenericPayload } from '../../../../common/types';
import { parseDepositAPIError, roundCurrency } from '../../payments.helpers';

interface BankDepositTableContent {
  bankDetails: string | undefined;
  reference: string | undefined;
  success: boolean;
}

@Component({
  selector: 'app-bank-deposit-method',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="id"
      [sourceDisplayMode]="sourceDisplayMode"
    >
      <app-source-template-header>
        <app-source-template-header-title i18n="Bank deposit method title">
          Bank Deposit
        </app-source-template-header-title>
        <app-source-template-logo [image]="bankLogo"></app-source-template-logo>
      </app-source-template-header>
      <app-source-template-body>
        <ng-container
          *ngIf="{
            formGroup: formGroup$ | flAsync,
            tableContent: tableContent$ | flAsync,
            countryOptions: countryOptions$ | flAsync
          } as wireContext"
        >
          <fl-bit *ngIf="wireContext.tableContent; else bankSpinner">
            <ng-container
              *ngIf="
                wireContext.tableContent.success;
                then bankDepositContent;
                else bankDepositError
              "
            ></ng-container>
            <ng-template #bankDepositContent>
              <fl-label
                i18n="Bank deposit method - country select label"
                [for]="'BankCountry'"
              >
                Select your country:
              </fl-label>
              <ng-container *ngIf="wireContext.formGroup">
                <ng-container
                  *ngIf="wireContext.formGroup.get('countrySelect') as control"
                >
                  <fl-select
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="bankDepositCountryDropdown"
                    [control]="control"
                    [flMarginBottom]="Margin.XSMALL"
                    [id]="'BankCountry'"
                    [options]="wireContext.countryOptions"
                  ></fl-select>
                </ng-container>
              </ng-container>
              <fl-bit class="BankInfo" [flMarginBottom]="Margin.XSMALL">
                <fl-text
                  i18n="Bank deposit method - bank information input label"
                  [weight]="FontWeight.BOLD"
                >
                  Bank Information
                </fl-text>
                <fl-bit
                  [innerHTML]="wireContext.tableContent.bankDetails"
                ></fl-bit>
              </fl-bit>
              <fl-bit class="BankInfo" [flMarginBottom]="Margin.XSMALL">
                <fl-text
                  i18n="Bank deposit method - payment reference input label"
                  [weight]="FontWeight.BOLD"
                >
                  Reference
                </fl-text>
                <fl-bit
                  [innerHTML]="wireContext.tableContent.reference"
                ></fl-bit>
              </fl-bit>
              <fl-text
                i18n="Bank deposit method - agreement copy"
                [flMarginBottom]="Margin.XSMALL"
              >
                Once you have deposited your funds, click the button to proceed.
                Enter your deposit details so we can identify your payment and
                finish the deposit faster. Please take a receipt or reference
                number from your bank after depositing.
              </fl-text>
              <ng-container *ngIf="wireContext.formGroup">
                <fl-label
                  i18n="Bank deposit method - bank name input label"
                  [for]="'BankName'"
                >
                  Bank account name you are depositing from:
                </fl-label>
                <ng-container
                  *ngIf="
                    wireContext.formGroup.get('bankAccountNameInput') as control
                  "
                >
                  <fl-input
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="bankDepositAccountInput"
                    [control]="control"
                    [flMarginBottom]="Margin.XSMALL"
                    [id]="'BankName'"
                  ></fl-input>
                </ng-container>
                <fl-label
                  i18n="Bank deposit method - deposit date input label"
                  [for]="'BankDate'"
                >
                  Date of deposit:
                </fl-label>
                <ng-container
                  *ngIf="
                    wireContext.formGroup.get('dateOfDepositInput') as control
                  "
                >
                  <fl-input
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="bankDepositDateInput"
                    i18n-placeholder="
                      Date input placeholder (don't change format)
                    "
                    placeholder="YYYY-MM-DD"
                    [flMarginBottom]="Margin.XSMALL"
                    [control]="control"
                    [id]="'BankDate'"
                  ></fl-input>
                </ng-container>
                <fl-label
                  i18n="Bank deposit method - deposit reference input label"
                  [for]="'BankReference'"
                >
                  Deposit reference (receipt or reference number):
                </fl-label>
                <ng-container
                  *ngIf="
                    wireContext.formGroup.get(
                      'depositReferenceInput'
                    ) as control
                  "
                >
                  <fl-input
                    *ngIf="isFormControl(control)"
                    flTrackingLabel="bankDepositReferenceInput"
                    [flMarginBottom]="Margin.XSMALL"
                    [control]="control"
                    [id]="'BankReference'"
                  ></fl-input>
                </ng-container>
                <fl-text
                  i18n="Bank deposit method - agreement copy"
                  [size]="TextSize.XXSMALL"
                >
                  Note: Any transaction fees charged by your bank will be
                  deducted from the total transfer amount. Funds will be
                  credited to your balance on the next business day after the
                  funds are received by Freelancer's bank. If you have any
                  questions please contact
                  <fl-link
                    flTrackingLabel="BankDepositSupportLink"
                    [link]="'/support'"
                    [newTab]="true"
                    [size]="TextSize.XXSMALL"
                  >
                    Online Support
                  </fl-link>
                </fl-text>
              </ng-container>
            </ng-template>
            <ng-template #bankDepositError>
              <fl-banner-alert
                i18n="Bank deposit setup error"
                [closeable]="false"
              >
                Bank Deposit is not currently available. Please contact support.
                <br />
                Event Id: {{ wireContext.tableContent.reference }}
              </fl-banner-alert>
            </ng-template>
          </fl-bit>
          <ng-template #bankSpinner
            ><fl-spinner
              *ngIf="confirmationFormValid$ | flAsync"
              flTrackingLabel="DepositBankInitialisationSpinner"
              [overlay]="true"
            ></fl-spinner
          ></ng-template>
        </ng-container>
      </app-source-template-body>
    </app-source-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./bank-deposit-method.component.scss'],
})
export class BankDepositMethodComponent implements OnInit, OnDestroy {
  Margin = Margin;
  TextSize = TextSize;
  FontWeight = FontWeight;
  HeadingWeight = HeadingWeight;
  HeadingType = HeadingType;
  isFormControl = isFormControl;
  PictureDisplay = PictureDisplay;
  ListItemType = ListItemType;
  SourceDisplayMode = SourceDisplayMode;

  @Input() countries$: Rx.Observable<readonly Country[]>;
  @Input() currencies$: Rx.Observable<readonly Currency[]>;
  @Input() depositFormGroup: FormGroup;
  @Input() genericPayload: GenericPayload;
  @Input() group: FormGroup;
  @Input() id: string;
  @Input() userInfo$: Rx.Observable<UsersSelf>;
  @Input() sourceDisplayMode: SourceDisplayMode;
  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  bankLogo: PictureImage = {
    alt: 'bank_deposit',
    src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.WIRE),
  };

  countryOptions$: Rx.Observable<readonly SelectItem[]>;
  formGroup$: Rx.Observable<FormGroup>;
  private subscriptions: readonly Rx.Subscription[] = [];
  tableContent$: Rx.Observable<BankDepositTableContent>;
  wireFormControls: { [key: string]: AbstractControl };
  confirmationFormValid$: Rx.Observable<boolean>;
  constructor(
    private fb: FormBuilder,
    private paymentsMessaging: PaymentsMessagingService,
    private deposits: Deposits,
    private location: Location,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    this.formGroup$ = Rx.combineLatest([
      this.userInfo$.pipe(map(userInfo => userInfo.address)),
      this.countries$,
    ]).pipe(
      map(([address, countries]) => {
        const userCountry = address
          ? countries.find(country => country.name === address.country)
          : countries[0];
        const userCountryCode = userCountry ? userCountry.code : undefined;
        const wireFormGroup = this.fb.group({
          countrySelect: [
            userCountryCode,
            [required($localize`Country is required!`)],
          ],
          bankAccountNameInput: [
            undefined,
            [required($localize`Bank account name is required!`)],
          ],
          dateOfDepositInput: [
            undefined,
            [
              required($localize`Date of deposit is required!`),
              pattern(
                /\d{4}-\d{2}-\d{2}/,
                $localize`Invalid date type, use YYYY-MM-DD`,
              ),
            ],
          ],
          depositReferenceInput: [
            undefined,
            [required($localize`Deposit Reference is required!`)],
          ],
        });
        this.wireFormControls = wireFormGroup.controls;
        return wireFormGroup;
      }),
      publishReplay(1),
      refCount(),
    );

    this.subscriptions = [
      ...this.subscriptions,
      this.formGroup$.subscribe(formGroup =>
        this.paymentsUtils.registerFormGroup(
          formGroup,
          this.depositFormGroup,
          this.id,
        ),
      ),
    ];

    this.countryOptions$ = this.countries$.pipe(
      map(countries =>
        countries.map((country: Country) => ({
          value: country.id,
          displayText: country.name,
        })),
      ),
    );

    const confirmationDepositForm$ = Rx.of(
      this.depositFormGroup.get(DepositForm.CONFIRMATION),
    );

    // FIXME: T69712..
    const selected$ = this.group.valueChanges.pipe(
      startWith(this.group.value),
      map(value => value[methodSelectionRadioControl] === this.id),
      distinctUntilChanged(),
    );
    this.confirmationFormValid$ = confirmationDepositForm$.pipe(
      filter(isDefined),
      switchMap(cf => cf.statusChanges),
      distinctUntilChanged(),
      map(confirmationFormValid => confirmationFormValid === 'VALID'),
    );

    const tableContentPayload$ = Rx.combineLatest([
      this.currencies$,
      selected$,
      confirmationDepositForm$.pipe(
        filter(isDefined),
        switchMap(cf => cf.valueChanges),
        distinctUntilChanged(
          (x, y) =>
            x.currencySelect === y.currencySelect &&
            x.depositAmountInput === y.depositAmountInput,
        ),
      ),
      this.formGroup$.pipe(
        filter(isDefined),
        map(formGroup => formGroup.get('countrySelect')),
        filter(isDefined),
        switchMap(countrySelect =>
          countrySelect.valueChanges.pipe(startWith(countrySelect.value)),
        ),
        distinctUntilChanged(),
      ),
    ]).pipe(
      map(
        ([currencies, selected, confirmationFormValue, countrySelectValue]) => {
          const currency = currencies.find(
            c => c.name === confirmationFormValue.currencySelect,
          );
          const confirmationForm = this.depositFormGroup.get(
            DepositForm.CONFIRMATION,
          );
          if (selected && currency && confirmationForm?.valid) {
            return this.getPostPayload(
              toNumber(roundCurrency(confirmationFormValue.depositAmountInput)),
              countrySelectValue,
              currency.id,
            );
          }
          return undefined;
        },
      ),
      filter(isDefined),
    );

    // FIXME: T69712..
    const tableContentCall$ = tableContentPayload$.pipe(
      switchMap(payload => this.deposits.deposit(payload)),
    );

    this.tableContent$ = tableContentCall$.pipe(
      map(response => {
        if (response.status === 'success') {
          const results = response.result;
          return {
            success: true,
            bankDetails: results.bank_details,
            reference: results.invoice
              ? results.invoice.description
              : undefined,
          };
        }
        return {
          success: false,
          bankDetails: undefined,
          reference: response.requestId,
        };
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  getPostPayload(
    amount: number,
    countryCode: string,
    currencyId: number,
  ): DepositRequestApi {
    return {
      ...this.genericPayload,
      domain: '',
      amount,
      currency_id: currencyId,
      country_code: countryCode,
      action: 'getPostForm',
      cancel_url: `${this.location.origin}/deposit/wire`,
      deposit_method: DepositMethodApi.WIRE,
    };
  }

  getPutPayload(invoice: DepositInvoiceApi): DepositRequestConfirmationApi {
    return {
      deposit_method: DepositMethodApi.WIRE,
      invoice_id: invoice.id,
      user_id: invoice.user_id,
      comments: this.wireFormControls.depositReferenceInput.value,
      sender_name: this.wireFormControls.bankAccountNameInput.value,
      sent_date: this.wireFormControls.dateOfDepositInput.value,
    };
  }

  handleDepositDataChanges(depositData: DepositData) {
    const payload = this.getPostPayload(
      depositData.paymentAmount,
      this.wireFormControls.countrySelect.value,
      depositData.paymentCurrency.id,
    );
    this.deposits
      .deposit(payload)
      .toPromise()
      .then(response => {
        if (response.status === 'success') {
          const { invoice } = response.result;
          if (!invoice) {
            // this should never happen? Add some monitoring here?
            this.paymentsMessaging.pushError({
              errorType: PaymentsErrorType.ERROR_UNKNOWN,
              eventId: response.requestId,
            });
            return;
          }
          const putPayload = this.getPutPayload(invoice);
          this.deposits
            .depositUpdate(putPayload)
            .then(putResponse => {
              if (putResponse.status === 'success') {
                this.paymentsMessaging.pushResult({
                  paymentsStatus: PaymentsResultStatus.PENDING,
                  paymentsMessage: `Your account will be credited ${depositData.paymentCurrency.sign}${depositData.paymentAmount} ${depositData.paymentCurrency.code}
                when we are notified of payment from our bank. Please expect up to 5 business days for your funds to clear.`,
                });
              } else {
                this.paymentsMessaging.pushError({
                  errorCode: putResponse.errorCode,
                  errorType: PaymentsErrorType.ERROR,
                  eventId: putResponse.requestId,
                });
              }
            })
            .catch(error =>
              this.paymentsMessaging.pushError(parseDepositAPIError(error)),
            );
        } else {
          this.paymentsMessaging.pushError({
            errorType: PaymentsErrorType.ERROR,
            errorCode: response.errorCode,
            eventId: response.requestId,
          });
        }
      })
      .catch(error =>
        this.paymentsMessaging.pushError(parseDepositAPIError(error)),
      );
  }
}
